import { memo, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TransactionTrackingDeliveryCode } from "features/shipmentTracking/types";

type Props = {
    readonly resource: TransactionTrackingDeliveryCode;
};

const Row = ({ label, children }: { readonly label: string; readonly children: ReactNode }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: '260px 1fr',
            alignItems: 'baseline',
            gap: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
        }}
    >
        <Typography variant='body2' color='text.secondary'>
            {label}
        </Typography>
        <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
            {children ?? '—'}
        </Typography>
    </Box>
);

const Section = ({ title, children }: { readonly title: string; readonly children: ReactNode }) => (
    <Box sx={{ mt: 2 }}>
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const format = (value: unknown): ReactNode => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    return String(value);
};

const ShipmentTrackingView = ({ resource }: Props) => (
    <Stack spacing={0}>
        <Section title='Merchant / Order data'>
            <Row label='Merchant'>{format(resource.merchantName)}</Row>
            <Row label='Merchant ID'>{format(resource.merchantId)}</Row>
            <Row label='Transaction ID'>{format(resource.transactionId)}</Row>
            <Row label='Internal Transaction ID'>{format(resource.idOfTransaction)}</Row>
            <Row label='Order ID'>{format(resource.orderId)}</Row>
            <Row label='Tracking number'>{format(resource.deliveryTrackingCode)}</Row>
            <Row label='Courier'>{format(resource.deliveryProvider)}</Row>
            <Row label='Tracking record ID'>{format(resource.trackingDeliveryCodeId)}</Row>
            <Row label='Submitted date'>{format(resource.createdAt)}</Row>
            <Row label='Updated at'>{format(resource.updatedAt)}</Row>
        </Section>

        <Section title='Transaction data'>
            <Row label='Transaction status'>{format(resource.transactionStatus)}</Row>
            <Row label='Fund status'>{format(resource.transactionFundStatus)}</Row>
            <Row label='Amount'>{format(resource.transactionAmount)}</Row>
            <Row label='Amount (EUR)'>{format(resource.transactionAmountEur)}</Row>
            <Row label='Currency'>{format(resource.currency)}</Row>
        </Section>

        <Section title='TrackingMore data'>
            <Row label='TrackingMore tracking ID'>{format(resource.trackingDeliveryCodeRefId)}</Row>
            <Row label='Current delivery status'>{format(resource.trackingDeliveryCodeStatus)}</Row>
            <Row label='Status reason'>{format(resource.trackingDeliveryCodeStatusReason)}</Row>
            <Row label='Tracking provider'>{format(resource.trackingDeliveryCodeTrackingProvider)}</Row>
            <Row label='Destination country'>{format(resource.destinationCountryIso2)}</Row>
            <Row label='TrackingMore destination country'>
                {format(resource.destinationTrackingDeliveryCodeCountryIso2)}
            </Row>
            <Row label='TrackingMore created at'>{format(resource.trackingDeliveryCodeCreatedAt)}</Row>
            <Row label='TrackingMore updated at'>{format(resource.trackingDeliveryCodeUpdatedAt)}</Row>
            <Row label='TrackingMore decisions'>{format(resource.trackingDeliveryCodeDecisions)}</Row>
            <Row label='TrackingMore notifications'>{format(resource.trackingDeliveryCodeNotifications)}</Row>
        </Section>
    </Stack>
);

export default memo(ShipmentTrackingView);
